import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Link as MuiLink,
  Badge,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import React, { useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { LogoTransparentWebp } from 'assets/icons';
import { useSubscription } from 'AppRoutes/context/SubscriptionContext';
import { useAuth } from 'services/auth/authContext';
import { useCart } from 'services/cart/CartContext';
import { useGrowlerCart } from 'services/cart/GrowlerCartContext';
import { auth } from 'services/firebase/client';
import {
  getStoreNavPath,
  isGrowlersPath,
  scrollToGrowlersCatalog,
} from 'utils/growlersFlow';

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  const { personalData, loading } = useSubscription();
  const beerCart = useCart();
  const growlerCart = useGrowlerCart();
  const isGrowlers = isGrowlersPath(location.pathname);
  const storeNavPath = getStoreNavPath();
  const cart = isGrowlers ? growlerCart.cart : beerCart.cart;
  const toggleCart = isGrowlers ? growlerCart.toggleCart : beerCart.toggleCart;
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleAccountClick = () => {
    setMobileMenuOpen(false);
    navigate('/login');
  };

  const isLoggedIn = Boolean(
    !loading && currentUser && !currentUser.isAnonymous,
  );
  const firstName =
    personalData?.fullName?.split(' ')[0] ||
    currentUser?.displayName?.split(' ')[0] ||
    currentUser?.email?.split('@')[0] ||
    '';

  const handleLogout = async () => {
    try {
      await auth.signOut();
      setMobileMenuOpen(false);
      void navigate('/login');
    } catch (error) {
      console.error('[Header]Erro ao fazer logout:', error);
    }
  };

  const handleNavigate = useCallback(
    (path: string) => {
      setMobileMenuOpen(false);
      navigate(path);
    },
    [navigate],
  );

  const handleStoreNavigate = useCallback(() => {
    setMobileMenuOpen(false);
    if (storeNavPath.includes('#') && location.pathname === '/growlers') {
      scrollToGrowlersCatalog();
      if (location.hash !== `#${storeNavPath.split('#')[1]}`) {
        navigate(storeNavPath);
      }
      return;
    }
    navigate(storeNavPath);
  }, [location.hash, location.pathname, navigate, storeNavPath]);

  return (
    <AppBar position="static" sx={{ background: '#1c1c1c', padding: '0 16px' }}>
      <Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
        {/* Mobile: menu sanduíche (esq) + logo (centro) + carrinho (dir) */}
        <Box
          sx={{
            display: { xs: 'flex', sm: 'none' },
            alignItems: 'center',
            width: '100%',
          }}
        >
          <IconButton
            color="inherit"
            aria-label="menu"
            onClick={() => setMobileMenuOpen(true)}
            sx={{ flex: '0 0 auto' }}
          >
            <MenuIcon />
          </IconButton>
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <img
              src={LogoTransparentWebp}
              alt="Cervejaria Logo"
              style={{
                width: 150,
                marginTop: 10,
                marginBottom: 10,
              }}
            />
          </Box>
          <IconButton
            size="large"
            color="inherit"
            aria-label="shopping cart"
            onClick={toggleCart}
            sx={{ flex: '0 0 auto' }}
          >
            <Badge
              badgeContent={cart.totalItems}
              color="error"
              sx={{
                '& .MuiBadge-badge': {
                  backgroundColor: '#ff4444',
                  color: 'white',
                  fontWeight: 'bold',
                },
              }}
            >
              <ShoppingCartIcon />
            </Badge>
          </IconButton>
        </Box>

        {/* Logo + título (desktop) */}
        <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center' }}>
          <img
            src={LogoTransparentWebp}
            alt="Cervejaria Logo"
            style={{
              marginRight: 16,
              width: 150,
              marginTop: 10,
              marginBottom: 10,
            }}
          />
          {/* <Typography
            variant="h5"
            component="div"
            sx={{
              color: '#F5F5F5',
            }}
          >
            Clube da 42
          </Typography> */}
        </Box>

        {/* Desktop: saudação + ícones */}
        <Box
          sx={{
            display: { xs: 'none', sm: 'flex' },
            alignItems: 'center',
          }}
        >
          <Typography variant="body1" sx={{ color: '#F5F5F5', marginRight: 2 }}>
            {loading ? (
              'Carregando...'
            ) : isLoggedIn ? (
              <>Olá, {firstName}</>
            ) : (
              <MuiLink
                onClick={handleAccountClick}
                sx={{
                  cursor: 'pointer',
                  color: '#F5F5F5',
                  textDecoration: 'underline',
                }}
              >
                Olá, faça seu login
              </MuiLink>
            )}
          </Typography>

          <IconButton
            size="large"
            edge="end"
            color="inherit"
            aria-label="shopping cart"
            onClick={toggleCart}
            sx={{ marginRight: 2 }}
          >
            <Badge
              badgeContent={cart.totalItems}
              color="error"
              sx={{
                '& .MuiBadge-badge': {
                  backgroundColor: '#ff4444',
                  color: 'white',
                  fontWeight: 'bold',
                },
              }}
            >
              <ShoppingCartIcon />
            </Badge>
          </IconButton>

          {!isLoggedIn && (
            <IconButton
              size="large"
              edge="end"
              color="inherit"
              aria-label="account"
              onClick={handleAccountClick}
            >
              <AccountCircleIcon />
            </IconButton>
          )}
          {isLoggedIn && (
            <IconButton
              size="large"
              edge="end"
              color="inherit"
              aria-label="logout"
              onClick={handleLogout}
            >
              <LogoutIcon />
            </IconButton>
          )}
        </Box>
      </Toolbar>

      {/* Drawer mobile: Entre/cadastre-se, Assinatura, Cervejas; se logado + opções da faixa preta */}
      <Drawer
        anchor="left"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
      >
        <Box sx={{ width: 280 }} role="presentation">
          <List>
            {!isLoggedIn && (
              <ListItem disablePadding>
                <ListItemButton onClick={handleAccountClick}>
                  <ListItemIcon>
                    <AccountCircleIcon />
                  </ListItemIcon>
                  <ListItemText primary="Entre ou cadastre-se" />
                </ListItemButton>
              </ListItem>
            )}
            <ListItem disablePadding>
              <ListItemButton onClick={() => handleNavigate('/')}>
                <ListItemText primary="Assinatura de Cerveja" />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton onClick={handleStoreNavigate}>
                <ListItemText primary="Cervejas" />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => handleNavigate('/cervejas-anteriores')}
              >
                <ListItemText primary="Cervejas anteriores" />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton onClick={() => handleNavigate('/envelhecidas')}>
                <ListItemText primary="Envelhecidas" />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton onClick={() => handleNavigate('/quem-somos')}>
                <ListItemText primary="Quem somos" />
              </ListItemButton>
            </ListItem>
            {isLoggedIn && (
              <>
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => handleNavigate('/mysubscription')}
                  >
                    <ListItemText primary="Minhas Assinaturas" />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemButton onClick={() => handleNavigate('/my-account')}>
                    <ListItemText primary="Minha Conta" />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemButton onClick={() => handleNavigate('/addresses')}>
                    <ListItemText primary="Meus endereços" />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemButton onClick={() => handleNavigate('/orders')}>
                    <ListItemText primary="Meus Pedidos" />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemButton onClick={() => handleNavigate('/support')}>
                    <ListItemText primary="Suporte" />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemButton onClick={handleLogout}>
                    <ListItemIcon>
                      <LogoutIcon />
                    </ListItemIcon>
                    <ListItemText primary="Sair" />
                  </ListItemButton>
                </ListItem>
              </>
            )}
          </List>
        </Box>
      </Drawer>
    </AppBar>
  );
};

export default Header;
